import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Bell, Check, Flame, Brain, Dumbbell, Heart, Trophy, CreditCard, AlertCircle, Pause } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { API } from "../App";
import { devWarn } from "../utils/devLogger";

const NotificationCenter = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      devWarn("Notifications fetch failed:", error?.message);
    }
  }, []);

  const generateNotifications = useCallback(async () => {
    try {
      await axios.post(`${API}/notifications/generate`);
      fetchNotifications();
    } catch (error) {
      devWarn("Notification generate failed:", error?.message);
    }
  }, [fetchNotifications]);

  useEffect(() => {
    fetchNotifications();
    generateNotifications();
    const interval = setInterval(() => {
      fetchNotifications();
      generateNotifications();
    }, 300000); // every 5 minutes
    return () => clearInterval(interval);
  }, [fetchNotifications, generateNotifications]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      devWarn("Mark read failed:", error?.message);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "streak": return <Flame className="w-4 h-4 text-orange-500" />;
      case "achievement": return <Trophy className="w-4 h-4 text-amber-500" />;
      case "physical": return <Dumbbell className="w-4 h-4 text-cyan-500" />;
      case "cognitive": return <Brain className="w-4 h-4 text-indigo-500" />;
      case "mindfulness": return <Heart className="w-4 h-4 text-pink-500" />;
      case "subscription": return <CreditCard className="w-4 h-4 text-violet-500" />;
      case "system": return <Pause className="w-4 h-4 text-slate-400" />;
      default: return <Bell className="w-4 h-4 text-sky-500" />;
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-sky-600 hover:text-sky-800 hover:bg-sky-100"
          data-testid="notification-bell-btn"
        >
          <Bell className="w-5 h-5" strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-sky-100">
          <span className="text-sm font-medium text-sky-800">
            {t("notifications.title", "Notifications")}
          </span>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-sky-500 hover:text-sky-700"
              data-testid="mark-all-read-btn"
            >
              {t("notifications.markAllRead", "Mark all read")}
            </button>
          )}
        </div>
        {notifications.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">
            {t("notifications.empty", "No notifications yet")}
          </div>
        ) : (
          <div>
            {notifications.slice(0, 10).map((n, idx) => (
              <div
                key={n.id || idx}
                className={`flex items-start gap-3 px-4 py-3 border-b border-sky-50 last:border-0 ${
                  !n.read ? "bg-sky-50/50" : ""
                }`}
              >
                <div className="mt-0.5">{getIcon(n.type)}</div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${!n.read ? "font-medium text-sky-800" : "text-slate-600"}`}>
                    {n.message}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">{timeAgo(n.created_at)}</p>
                </div>
                {!n.read && <div className="w-2 h-2 bg-sky-400 rounded-full mt-1.5 flex-shrink-0" />}
              </div>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationCenter;
